/**
 * UCM 入向待确认记录列表（同源合并展示）
 *
 * 数据源是"组"（一封报文一组；含 IN+OUT 双段的报文合并成一行）：
 *   - 方向列：按 members[] 渲染多个标签（如 `OUT 41` + `IN 空`）；
 *   - 板数列：组内累加；
 *   - 状态列：用组级 status_summary（pending / confirmed / ignored / partial / parse_failed）；
 *   - 操作列：查看传整组给抽屉；
 *     单段时确认 / 忽略沿用单条 pending；多段不提供"跨段操作"（规格非目标），需要进详情逐段操作。
 */

import React from 'react';
import { Table, Tag, Space, Button, Popconfirm, Tooltip } from 'antd';
import { EyeOutlined, CheckCircleOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    UCM_INBOUND_SOURCE,
    UCM_INBOUND_GROUP_STATUS,
    UCM_INBOUND_MATCH_STATUS,
    canConfirmInbound,
    canReopenInbound,
    UCM_REOPEN_TEXT,
    memberDirectionTag,
} from '../constants/ucmConstants';

const renderTag = (map, value) => {
    const item = map[value] || {};
    return <Tag color={item.color}>{item.label || value || '-'}</Tag>;
};

// 兼容老调用方（万一传进来的是单条 pending 记录而不是分组结构），按一组只含自己一段处理
const normalizeRowToGroup = (row) => {
    if (!row) return null;
    if (Array.isArray(row.members)) return row;
    // 老的 flat 结构 → 包装成单段组（仅前端兼容用，不应在分组接口下出现）
    return {
        group_key: `flat:${row.id}`,
        primary_pending_id: row.id,
        flight_number: row.flight_number,
        flight_date: row.flight_date,
        origin_airport: row.origin_airport,
        source: row.source,
        status_summary: row.status,
        match_status_summary: row.match_status,
        created_at: row.created_at,
        members: [{
            id: row.id,
            direction: row.direction,
            status: row.status,
            transport_mode: row.transport_mode,
            match_status: row.match_status,
            uld_total: row.uld_total,
            uld_known: row.uld_known,
            uld_unknown: row.uld_unknown,
            surface_trip_number: row.surface_trip_number,
            matched_flight_op_id: row.matched_flight_op_id,
            created_at: row.created_at,
        }],
    };
};

const sumMembers = (members, key) =>
    (members || []).reduce((acc, m) => acc + Number(m?.[key] ?? 0), 0);

const UcmInboundTable = ({
    dataSource,
    loading,
    pagination,
    onChange,
    onView,
    onConfirm,
    onIgnore,
    onReopen,
}) => {
    const groups = (dataSource || []).map(normalizeRowToGroup).filter(Boolean);

    const columns = [
        {
            title: 'ID',
            key: 'id',
            width: 70,
            fixed: 'left',
            render: (_, g) => g.primary_pending_id,
        },
        {
            title: '来源',
            dataIndex: 'source',
            key: 'source',
            width: 100,
            render: (v) => renderTag(UCM_INBOUND_SOURCE, v),
        },
        {
            title: '方向',
            key: 'direction',
            width: 170,
            render: (_, g) => (
                <Space size={4} wrap>
                    {(g.members || []).map((m) => {
                        const t = memberDirectionTag(m);
                        return (
                            <Tag
                                color={t.color}
                                key={m.id}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onView && onView(g, m.id);
                                }}
                            >
                                {t.label}
                            </Tag>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: '航班匹配',
            key: 'match_status_summary',
            width: 240,
            render: (_, g) => {
                const status = g.match_status_summary;
                const info = g.matched_flight_info;
                return (
                    <Space direction="vertical" size={0}>
                        {renderTag(UCM_INBOUND_MATCH_STATUS, status)}
                        {info && (
                            <span style={{ fontSize: 12, color: '#666' }}>
                                {info.flight_number}{' '}
                                {info.flight_date ? dayjs(info.flight_date).format('MM-DD') : ''}{' '}
                                {info.departure_airport || '?'}→{info.arrival_airport || '?'}
                            </span>
                        )}
                    </Space>
                );
            },
        },
        { title: '报文航班号', dataIndex: 'flight_number', key: 'flight_number', width: 110, render: (v) => v || '-' },
        { title: '报文机场', dataIndex: 'origin_airport', key: 'origin_airport', width: 90, render: (v) => v || '-' },
        {
            title: '航班日期',
            dataIndex: 'flight_date',
            key: 'flight_date',
            width: 120,
            render: (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '-'),
        },
        {
            title: '板数（可写/总）',
            key: 'uld',
            width: 140,
            render: (_, g) => {
                const known = sumMembers(g.members, 'uld_known');
                const total = sumMembers(g.members, 'uld_total');
                const unknown = sumMembers(g.members, 'uld_unknown');
                return (
                    <span>
                        {known}/{total}
                        {unknown > 0 && (
                            <Tooltip title={`${unknown} 块台账中无此板号，确认时将跳过`}>
                                <Tag color="warning" style={{ marginLeft: 6 }}>未知 {unknown}</Tag>
                            </Tooltip>
                        )}
                    </span>
                );
            },
        },
        {
            title: '状态',
            dataIndex: 'status_summary',
            key: 'status_summary',
            width: 110,
            render: (v) => renderTag(UCM_INBOUND_GROUP_STATUS, v),
        },
        {
            title: '解析时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            render: (d) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm:ss') : '-'),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 220,
            render: (_, g) => {
                const members = g.members || [];
                const isSingle = members.length === 1;
                // 单段：沿用单条 pending 的"确认 / 忽略"；多段：列表不做跨段批量操作，需进详情逐段做
                const lone = isSingle ? members[0] : null;
                const confirmable = isSingle && canConfirmInbound(lone);
                const ignorable = isSingle && lone && (lone.status === 'pending' || lone.status === 'parse_failed');
                const reopenable = isSingle && canReopenInbound(lone);
                const multiTip = isSingle ? '' : '本封报文含多段（IN+OUT），请进详情按段操作';
                return (
                    <Space size="small">
                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onView(g)}>
                            查看
                        </Button>
                        <Popconfirm
                            title="确认写入台账"
                            description="将按报文更新对应 ULD 的位置，确认后不可撤销"
                            okText="确认"
                            cancelText="取消"
                            disabled={!confirmable}
                            onConfirm={() => onConfirm({ id: lone.id })}
                        >
                            <Tooltip title={confirmable ? '' : (multiTip || '仅「待确认 + 已匹配航班」可确认')}>
                                <Button type="link" size="small" icon={<CheckCircleOutlined />} disabled={!confirmable}>
                                    确认
                                </Button>
                            </Tooltip>
                        </Popconfirm>
                        {(reopenable || (!isSingle && g.status_summary === 'ignored')) && (
                            <Popconfirm
                                title="重新处理该记录"
                                description={UCM_REOPEN_TEXT.recordConfirm}
                                okText="重新处理"
                                cancelText="取消"
                                disabled={!reopenable}
                                onConfirm={() => onReopen({ id: lone.id })}
                            >
                                <Tooltip title={reopenable ? UCM_REOPEN_TEXT.recordConfirm : multiTip}>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<UndoOutlined />}
                                        disabled={!reopenable}
                                    >
                                        {UCM_REOPEN_TEXT.recordAction}
                                    </Button>
                                </Tooltip>
                            </Popconfirm>
                        )}
                        <Popconfirm
                            title="忽略该记录"
                            description="忽略后不再处理，可在列表按状态查回"
                            okText="忽略"
                            cancelText="取消"
                            disabled={!ignorable}
                            onConfirm={() => onIgnore({ id: lone.id })}
                        >
                            <Tooltip title={ignorable ? '' : multiTip}>
                                <Button type="link" size="small" danger icon={<StopOutlined />} disabled={!ignorable}>
                                    忽略
                                </Button>
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={groups}
            loading={loading}
            pagination={pagination}
            onChange={onChange}
            rowKey={(g) => g.group_key || `pid:${g.primary_pending_id}`}
            scroll={{ x: 1840 }}
            size="small"
        />
    );
};

export default UcmInboundTable;
